import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useUpdateEstimateFieldMutation } from "@/hooks/mutation/estimates/useUpdateEstimateFieldMutation";
import { useUpdateMitigationDetailsMutation } from "@/hooks/mutation/estimates/useUpdateMitigationDetailsMutation";
import { syncEstimateDetailFromServerToRedux } from "@/hooks/estimate/syncEstimateDetailFromServer";
import { ESTIMATE_KEYS } from "@/hooks/queries/estimates/queryKeys";
import { estimationActions } from "@/redux/actions/estimation.actions";
import { store, useDispatch } from "@/redux/store";
import type { Estimation } from "@/interfaces/estimation.interface";
import { buildLineTextByUniqueIdMap } from "@/components/estimation-details/cat3-sync/cat3LineItemDiff";
import { useCat3LineHighlightOptional } from "@/components/estimation-details/cat3-sync/Cat3LineHighlightContext";

export function useUpdateEstimationDetails(estimateId: string) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { mutateAsync: updateEstimateField } = useUpdateEstimateFieldMutation();
  const { mutateAsync: updateMitigationDetails } = useUpdateMitigationDetailsMutation();
  const cat3LineHighlight = useCat3LineHighlightOptional();

  const updateEstimationDetails = useCallback(
    async (estimation: Estimation, field: keyof Estimation, clientOnly: boolean = false) => {
      const previousEstimation = queryClient.getQueryData<Estimation | null>(ESTIMATE_KEYS.detailMetadata(estimateId));

      // Optimistic update on both caches
      queryClient.setQueryData(ESTIMATE_KEYS.detailMetadata(estimateId), estimation);
      dispatch(estimationActions.setEstimationState({ estimation }));

      if (clientOnly) return true;

      try {
        if (field === "mitigationDetails") {
          const { lineItemSyncQueued, jobId } = await updateMitigationDetails({
            estimateId,
            mitigationDetails: estimation.mitigationDetails ?? {},
          });

          if (lineItemSyncQueued) {
            queryClient.invalidateQueries({ queryKey: ESTIMATE_KEYS.detail(estimateId) });
            const beforeRooms = store.getState().estimation.estimationDetail;
            const beforeMap = buildLineTextByUniqueIdMap(beforeRooms ?? undefined);
            // The job id is what lets the provider watch this exact recompute and report a worker
            // failure; without one there is nothing to track, so fall back to a plain refetch.
            if (cat3LineHighlight && jobId) {
              cat3LineHighlight.beginCatSync(beforeMap, jobId);
              toast.info("Updating line items for the new category…");
            } else {
              await syncEstimateDetailFromServerToRedux(dispatch, estimateId, { silent: true });
            }
          }
        } else {
          await updateEstimateField({
            estimateId,
            updates: { [field]: estimation[field] },
          });
        }

        return true;
      } catch {
        // Revert both caches on error
        queryClient.setQueryData(ESTIMATE_KEYS.detailMetadata(estimateId), previousEstimation);
        if (previousEstimation) {
          dispatch(estimationActions.setEstimationState({ estimation: previousEstimation }));
        }
        return false;
      }
    },
    [cat3LineHighlight, dispatch, estimateId, queryClient, updateEstimateField, updateMitigationDetails],
  );

  return { updateEstimationDetails };
}
